/**
 * Passive "a new version is available" notice, in the style of the Aspire and Azure CLIs.
 *
 * The notice is printed from a locally cached marker, so it never adds latency. When that marker is
 * stale it kicks off a *detached* background process (the hidden `__update-check` command) that
 * refreshes the cache for the next invocation — the current command exits immediately either way.
 */
import { spawn } from 'node:child_process'
import { getCliVersion } from '../version.js'
import { fetchLatestVersion } from './registry.js'
import { isNewerVersion } from './semver.js'
import { isStale, readUpdateState, writeUpdateState } from './state.js'

/** Commands for which a trailing update notice would be noise or self-referential. */
const SUPPRESSED_COMMANDS = new Set(['update', '__update-check', 'help'])

/**
 * Prints an update notice if the cache knows of a newer version, then refreshes the cache in the
 * background when it is stale. Never throws — update checks must not affect command exit status.
 */
export async function notifyOnExit(argv: string[]): Promise<void> {
  if (isUpdateCheckSuppressed(argv)) return
  try {
    const current = getCliVersion()
    const state = await readUpdateState()
    if (state?.latestVersion && isNewerVersion(state.latestVersion, current)) {
      printUpdateNotice(current, state.latestVersion)
    }
    if (isStale(state)) spawnBackgroundRefresh()
  } catch {
    // A failed update check is never worth surfacing.
  }
}

/** Body of the hidden `__update-check` command: refresh the cached marker, silently. */
export async function runUpdateCheckRefresh(): Promise<void> {
  const latest = await fetchLatestVersion({ timeoutMs: 4000 }).catch(() => null)
  // Record the attempt regardless of outcome so we back off for the full interval on failure too.
  await writeUpdateState({ lastCheckAt: new Date().toISOString(), latestVersion: latest ?? undefined }).catch(() => {})
}

/** Whether the passive notice (and background refresh) should be skipped for this invocation. */
export function isUpdateCheckSuppressed(argv: string[]): boolean {
  if (isEnvDisabled()) return true
  if (process.env.CI) return true
  if (!process.stderr.isTTY) return true // keep piped/scripted output clean

  const args = argv.slice(2)
  if (args.length === 0) return true // bare `verentis` prints help
  if (args.some((a) => a === '--json' || a === '-h' || a === '--help' || a === '-V' || a === '--version')) return true

  const command = args.find((a) => !a.startsWith('-'))
  return command !== undefined && SUPPRESSED_COMMANDS.has(command)
}

function isEnvDisabled(): boolean {
  const off = new Set(['0', 'false', 'no', 'off'])
  const noCheck = process.env.VERENTIS_NO_UPDATE_CHECK
  if (noCheck !== undefined && noCheck !== '' && !off.has(noCheck.toLowerCase())) return true
  const explicit = process.env.VERENTIS_UPDATE_CHECK
  return explicit !== undefined && off.has(explicit.toLowerCase())
}

function printUpdateNotice(current: string, latest: string): void {
  const message = [
    `Update available ${current} → ${latest}`,
    'Run `verentis update` to install the latest version.',
  ]
  const width = Math.max(...message.map((line) => line.length))
  const border = '─'.repeat(width + 2)
  const lines = ['', `╭${border}╮`, ...message.map((line) => `│ ${line.padEnd(width)} │`), `╰${border}╯`, '']
  process.stderr.write(lines.join('\n') + '\n')
}

function spawnBackgroundRefresh(): void {
  try {
    const entry = process.argv[1]
    if (!entry) return
    const child = spawn(process.execPath, [entry, '__update-check'], {
      detached: true,
      stdio: 'ignore',
      windowsHide: true,
    })
    child.on('error', () => {}) // never let a failed spawn bubble up
    child.unref()
  } catch {
    // ignore — the notice will simply refresh on a later run
  }
}
